import { Component, Injector, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { FormControlEx } from '../forms/form-control';
import { InputControlBase } from '../forms/input-control.base';
import { TranslateDialogFormComponent } from '../translate-dialog-form/translate-dialog-form.component';
import { Phrase } from '../../model/phrase';
import { Translation } from '../../model/translation';
import { ActionWindowService } from '../../service/action-window.service';
import { FormControlExFactory } from '../../service/factory/form-control-ex-factory.service';
import { ENGLISH_CULTURE, LanguageService, SWEDISH_CULTURE } from '../../service/language.service';

@Component({
    selector: 'letting-translation-control',
    template: `
    <astral-field-shell [control]="control" [forId]="inputId" [helpText]="helpText" [errorMessage]="errorMessage">
      <div class="letting-translation-control" (focusin)="onfocus()" (focusout)="onblur()">
        @if (type === 'area') {
          <astral-textarea
            [control]="innerControl"
            [accessibilityControl]="control"
            [helpText]="helpText"
            [inputId]="inputId"
            [withShell]="false"
            [rows]="5"
          ></astral-textarea>
        } @else {
          <astral-input
            [control]="innerControl"
            [accessibilityControl]="control"
            [helpText]="helpText"
            [inputId]="inputId"
            [withShell]="false"
          ></astral-input>
        }

        <button
          class="letting-translation-control__translate-button"
          type="button"
          [attr.aria-label]="'TRANSLATE.TRANSLATE_BUTTON' | translate"
          [title]="'TRANSLATE.TRANSLATE_BUTTON' | translate"
          (click)="translateClicked()"
        >
          <i class="fa-light fa-language" aria-hidden="true"></i>
        </button>
      </div>
    </astral-field-shell>
    `,
    styleUrls: ['letting-translation-control.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class LettingTranslationControl extends InputControlBase<Translation[]> implements OnInit, OnDestroy {
  private static nextId = 0;
  readonly inputId = `letting-translation-${++LettingTranslationControl.nextId}`;

  @Input() type: 'box' | 'area' = 'box';
  @Input() requiredCulture?: string;

  protected innerControl: FormControlEx;

  private cultures = [SWEDISH_CULTURE, ENGLISH_CULTURE];
  private currentCulture: string;
  private destroy = new Subject<void>();

  constructor(
    readonly fcf: FormControlExFactory,
    private readonly languageService: LanguageService,
    private readonly dialogService: ActionWindowService,
    private readonly injector: Injector
  ) {
    super();

    this.innerControl = fcf.create('', []);
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.innerControl.isReadonly = this.control.isReadonly;
    this.innerControl.required = this.control.required;

    this.updateCurrentCulture();
    this.updateInnerControl();

    this.control.valueChanges.pipe(takeUntil(this.destroy)).subscribe({
      next: () => this.updateInnerControl(),
    });

    this.languageService.onLangChange.pipe(takeUntil(this.destroy)).subscribe({
      next: () => {
        this.updateCurrentCulture();
        this.updateInnerControl();
      },
    });

    this.innerControl.valueChanges.pipe(takeUntil(this.destroy), debounceTime(500)).subscribe({
      next: val => {
        let change = this.controlValue.filter(x => x.cultureName !== this.currentCulture);
        change.push({
          cultureName: this.currentCulture,
          translationText: val,
        });

        this.control.setValue(change);
        this.control.markAsDirty();
      },
    });

    this.control.touchStateChanged.pipe(takeUntil(this.destroy)).subscribe({
      next: () => this.innerControl.markAsTouched(),
    });
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();

    this.destroy.next();
  }

  translateClicked() {
    this.dialogService
      .open(
        TranslateDialogFormComponent,
        this.injector,
        null,
        {
          phrase: new Phrase(this.controlValue, this.languageService),
          cultures: this.cultures,
          type: this.type,
          isReadonly: this.control.isReadonly,
          requiredCulture: this.requiredCulture,
          atLeastOne: this.control.required && !this.requiredCulture,
          cannotRemove: this.control.required && !this.requiredCulture,
        },
        this.type === 'area'
      )
      .pipe(takeUntil(this.destroy))
      .subscribe({
        next: (result: Phrase) => {
          if (this.control.isReadonly) {
            return;
          }

          this.control.setValue(result.translations.filter(x => !!x.translationText));
          this.control.markAsDirty();
        },
      });
  }

  private updateCurrentCulture() {
    this.currentCulture = this.cultures.includes(this.languageService.currentCultureLang)
      ? this.languageService.currentCultureLang
      : SWEDISH_CULTURE;
  }

  private updateInnerControl() {
    const text = this.controlValue.find(x => x.cultureName === this.currentCulture)?.translationText || '';
    this.innerControl.setValue(text, { emitEvent: false });
  }

  private get controlValue() {
    return Array.isArray(this.control.value) ? this.control.value : [];
  }
}
